import { observer } from "mobx-react-lite";
import { Button } from "antd";
import PageBox from "../../layout/PageBox";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { heart_bottle_icon, mi, program_selection_frame_christmas_long_bg } from "../../imgs";
import { MyColor } from "../../config/myStyle";
import { campaignStore, userStore } from "../../stores";
import { useEffect, useState } from "react";
import ShareCampaign from "../../components/ShareCampaign";
import { feedbackRequest } from "../../api/feedback";

import { useTranslation } from "react-i18next";

type Payment = { campaignId: string; status: "pending" | "success" | "failed"; paymentMethod: string };

const mainBlue = "#164194";
const btnColor = "#bfa373"; // 按钮棕色

const DonationResult = observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { campaignId } = useParams();
    const campaign = campaignStore.campaignMap.get(campaignId ?? '')
    const campaignStatus = campaignStore.campaignStatusMap.get(campaignId ?? '')

    const [params] = useSearchParams();
    const donationId = params.get('donationId');
    const [payment, setPayment] = useState<Payment>();
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (campaignId) campaignStore.getCampaign(campaignId);
    }, [campaignId]);
    useEffect(() => {
        // 恢復原有版面，同時保留實際付款查詢；網址回跳不能代表成功。
        setPayment(undefined); setError(false); setLoading(true);
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    // 查詢真實捐款狀態；回跳網址及感謝頁本身不代表付款成功。
    const check = async () => {
      if (!donationId) { setError(true); setLoading(false); return; }
      try {
        const token = localStorage.getItem(`fa_donation_${donationId}`) || '';
        const current: Payment = await feedbackRequest(`/donation/v1/${encodeURIComponent(donationId)}/payment-status`, { authToken: token }, controller.signal);
        if (current.campaignId !== campaignId) throw new Error('Mismatched campaign');
        if (controller.signal.aborted) return;
        setPayment(current); setError(false);
        if (current.status === 'success' && localStorage.getItem('jwt_token')) userStore.getUser();
        if (current.status === 'pending' && current.paymentMethod === 'payme' && ++attempts < 30) timer = setTimeout(check, 2000);
      } catch { if (!controller.signal.aborted) setError(true); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    };
    check();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [donationId, campaignId]);
    const state = error ? 'unknown' : payment?.status || 'pending';
    const detail = state === 'pending' && payment?.paymentMethod !== 'payme' && payment ? 'offline' : state;

    return (
        <PageBox spinning={loading || campaignStatus?.loading}>
            <div
                className="w-full flex flex-col items-center justify-center px-[20px] pt-[50px]"
                style={{
                    backgroundImage: `url(${program_selection_frame_christmas_long_bg})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                }}
            >
                {/* 气泡和内容区域 */}
                <div
                    style={{
                        width: "100%",
                        backgroundColor: 'white',
                        borderColor: MyColor.blue,
                        borderWidth: 3,
                        borderRadius: 30,
                        padding: "24px 18px",
                        boxSizing: "border-box",
                        position: 'relative',
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        {/* 成功時保留原感謝語；其他狀態沿用同一品牌版面顯示實際結果。 */}
                        <div
                            style={{
                                color: mainBlue,
                                fontWeight: 600,
                                fontSize: 15,
                                textAlign: "center",
                                lineHeight: 1.3,
                                wordBreak: "break-word",
                            }}
                        >
                            {state === 'success'
                                ? t("donation_result.thanks", { title: campaign?.title ?? '' })
                                : <><div className="text-[21px] mb-3" role="status">{t(`payment_result.${state}`)}</div>{t(`payment_result.${detail}_detail`)}</>}
                        </div>
                        {/* 气泡内icon */}
                        {state === 'success' && <img
                            src={heart_bottle_icon}
                            alt="heart in bottle"
                            style={{
                                height: 135,
                                marginTop: 25,
                            }}
                        />}
                        {/* 按钮区 */}
                        <div className="w-full flex justify-center gap-[28px] mt-[30px] mb-[4px]" style={{ pointerEvents: "auto" }}>
                            <Button
                                className="flex-1"
                                style={{
                                    background: btnColor,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 28,
                                    fontWeight: 500,
                                    fontSize: 21,
                                    height: 50,
                                }}
                                onClick={() => navigate("/")}
                            >
                                {t("donation_result.home")}
                            </Button>
                            {state === 'success' && <ShareCampaign campaign={campaign} variant="result" />}
                        </div>
                    </div>
                </div>
                {/* 天使公仔区域 */}
                <div className="w-full flex flex-col items-center mt-[35px] mb-[17px]">
                    <img
                        src={mi}
                        alt="angel mascot"
                        style={{
                            height: 210,
                        }}
                    />
                </div>
            </div>
        </PageBox>
    );
});

export default DonationResult;
